// Activity events emitted by the calendar module itself. These cover
// the LIFECYCLE of calendar_events rows (create / update / cancel /
// materialise / etc.) — distinct from the upstream events that
// TRIGGER materialisation (article.scheduled, survey.opened, etc.),
// which the calendar SUBSCRIBES to.
//
// Subject convention:
//   - All calendar lifecycle events have subjectKind='calendar-event'
//     and subjectId=<calendar event id>. A subscriber wanting "every
//     calendar mutation" subscribes to the kind glob below; one
//     wanting per-source events should subscribe to the SOURCE
//     module's kind glob (e.g. `article.*`).
import { z } from 'zod';
import { CalendarEventStatus } from './event.js';
export const CALENDAR_ACTIVITY_KINDS = {
    EventCreated: 'calendar.event-created',
    EventUpdated: 'calendar.event-updated',
    EventCancelled: 'calendar.event-cancelled',
    EventReminder: 'calendar.event-reminder',
};
/** Glob pattern matching every calendar-emitted kind. */
export const CALENDAR_ACTIVITY_KIND_GLOB = 'calendar.*';
const Iso = z.string().datetime({ offset: true });
export const CalendarEventCreatedEvent = z.object({
    type: z.literal('calendar.event-created'),
    eventId: z.string().uuid(),
    subjectKind: z.string(),
    subjectId: z.string(),
    /** Who triggered the create. NULL for system-materialised events
     *  (the activity row's actorKind is 'system' in that case). */
    createdBy: z.string().uuid().nullable(),
    startsAt: Iso,
    occurredAt: Iso,
});
export const CalendarEventUpdatedEvent = z.object({
    type: z.literal('calendar.event-updated'),
    eventId: z.string().uuid(),
    subjectKind: z.string(),
    subjectId: z.string(),
    /** Names of changed fields the request actually mutated. Empty
     *  array means a no-op PATCH (caller passed identical values). */
    changedFields: z.array(z.string()),
    occurredAt: Iso,
});
export const CalendarEventCancelledEvent = z.object({
    type: z.literal('calendar.event-cancelled'),
    eventId: z.string().uuid(),
    subjectKind: z.string(),
    subjectId: z.string(),
    /** Distinguishes USER-initiated cancellation (DELETE on a custom
     *  event) from SOURCE-driven auto-cancellation (article schedule
     *  cancelled, article archived, etc.). Useful for analytics + for
     *  notifications that should fire only on user action. */
    reason: z.enum(['user-deleted', 'source-cancelled']),
    /** Carries the previous status so a subscriber can distinguish
     *  cancelling-a-future-event from cancelling-a-live-event. */
    previousStatus: CalendarEventStatus,
    occurredAt: Iso,
});
export const CalendarEventReminderEvent = z.object({
    type: z.literal('calendar.event-reminder'),
    eventId: z.string().uuid(),
    title: z.string(),
    startsAt: Iso,
    reminderAt: Iso,
    minutesBefore: z.number().int().positive(),
    recipientId: z.string().uuid(),
    occurredAt: Iso,
});
//# sourceMappingURL=events.js.map